import PropTypes from 'prop-types'
import { useMemo, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Tooltip,
  Typography
} from '@material-tailwind/react'
import {
  FiFile,
  FiFileText,
  FiImage,
  FiGrid,
  FiAlertTriangle,
  FiPaperclip,
  FiX
} from 'react-icons/fi'
import { getMassiveImportFilesValidationSummary } from '@/utils/massive-import-file-validation'

const getFileIcon = (file) => {
  const contentType = file?.content_type || ''
  const filename = String(file?.filename || '').toLowerCase()

  if (contentType === 'application/pdf' || filename.endsWith('.pdf')) {
    return <FiFileText className='text-red-600' size={18} />
  }

  if (contentType.startsWith('image/') || /\.(jpg|jpeg|png|tif|tiff|bmp|heic)$/i.test(filename)) {
    return <FiImage className='text-blue-600' size={18} />
  }

  if (
    contentType.includes('sheet') ||
    contentType.includes('excel') ||
    /\.(xls|xlsx|csv)$/i.test(filename)
  ) {
    return <FiGrid className='text-green-600' size={18} />
  }

  return <FiFile className='text-gray-600' size={18} />
}

const buttonClassNamesByStatus = {
  valid: 'bg-blue-50 text-blue-600',
  warning: 'bg-amber-50 text-amber-700',
  error: 'bg-red-50 text-red-600'
}

const alertClassNamesByStatus = {
  warning: 'bg-amber-50 text-amber-700',
  error: 'bg-red-50 text-red-600'
}

export const MassiveImportFilesList = ({ files, onClear, onRemoveFile, onAddFiles, accept, validationType }) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef(null)
  const totalFiles = files.length
  const validationSummary = useMemo(
    () => getMassiveImportFilesValidationSummary(files, validationType),
    [files, validationType]
  )
  const invalidTooltipText = validationSummary.invalidCount === validationSummary.totalFiles
    ? 'Ningun archivo cumple el formato de nombre esperado.'
    : 'Algunos archivos no cumplen el formato de nombre esperado.'

  if (totalFiles === 0) return null

  const handleToggle = () => setOpen((currentValue) => !currentValue)
  const handleAddFilesClick = () => inputRef.current?.click()

  return (
    <>
      <div className='inline-flex items-center justify-center align-middle'>
        <Tooltip
          content={`${totalFiles} ${totalFiles === 1 ? 'archivo seleccionado' : 'archivos seleccionados'}. Haz click para revisar el detalle.`}
          placement='top'
          className='bg-blue-500'
        >
          <Button
            type='button'
            variant='text'
            className={`flex h-10 w-10 items-center justify-center rounded-full p-0 text-sm font-semibold leading-none ${buttonClassNamesByStatus[validationSummary.status]}`}
            onClick={handleToggle}
          >
            {totalFiles}
          </Button>
        </Tooltip>
        {validationSummary.invalidCount > 0 && (
          <Tooltip
            content={invalidTooltipText}
            placement='top'
            className='max-w-xs whitespace-pre-line border border-slate-200 bg-white text-sm text-slate-900 shadow-lg'
          >
            <Button
              type='button'
              variant='text'
              className={`ml-2 flex h-10 w-10 items-center justify-center rounded-full p-0 ${alertClassNamesByStatus[validationSummary.status]}`}
              onClick={handleToggle}
            >
              <FiAlertTriangle size={18} />
            </Button>
          </Tooltip>
        )}
      </div>

      <Dialog open={open} handler={handleToggle} size='md' className='p-3'>
        <DialogHeader className='flex items-start gap-3 pb-2'>
          <div className='rounded-2xl bg-blue-50 p-3 text-blue-700'>
            <FiPaperclip size={20} />
          </div>
          <div>
            <Typography variant='h6' className='text-black text-lg'>
              Archivos por subir
            </Typography>
            <Typography variant='small' className='mt-1 text-gray-600'>
              Revisa el detalle de los archivos seleccionados antes de cargar.
            </Typography>
          </div>
        </DialogHeader>

        <DialogBody className='max-h-[70vh] overflow-y-auto pt-2'>
          <input
            ref={inputRef}
            type='file'
            multiple
            accept={accept}
            className='hidden'
            onChange={(event) => {
              const selectedFiles = Array.from(event.target.files || [])
              if (selectedFiles.length > 0) {
                onAddFiles(selectedFiles)
              }
              event.target.value = ''
            }}
          />
          <div className='mb-4 flex justify-end'>
            <Button
              type='button'
              variant='outlined'
              color='blue'
              onClick={handleAddFilesClick}
              className='normal-case'
            >
              Agregar archivos
            </Button>
          </div>
          <div className='rounded-lg border border-gray-200 bg-gray-50'>
            {files.map((file, index) => (
              <div
                key={`${file.filename}-${index}`}
                className='flex items-center gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0'
              >
                <div className='rounded-full bg-white p-2 shadow-sm'>
                  {getFileIcon(file)}
                </div>
                <Typography variant='small' className='flex-1 break-all text-gray-900'>
                  {file.filename}
                </Typography>
                {validationSummary.invalidFiles.some((item) => item.index === index) && (
                  <Tooltip
                    content={validationSummary.invalidFiles.find((item) => item.index === index)?.reason}
                    placement='top'
                    className='max-w-xs whitespace-pre-line border border-slate-200 bg-white text-sm text-slate-900 shadow-lg'
                  >
                    <div className='rounded-full bg-amber-50 p-2 text-amber-700'>
                      <FiAlertTriangle size={16} />
                    </div>
                  </Tooltip>
                )}
                <Button
                  type='button'
                  size='sm'
                  variant='text'
                  color='red'
                  className='min-w-0 rounded-full p-2'
                  onClick={() => onRemoveFile(index)}
                >
                  <FiX size={16} />
                </Button>
              </div>
            ))}
          </div>
        </DialogBody>

        <DialogFooter className='flex justify-between'>
          <Button
            type='button'
            color='red'
            variant='outlined'
            onClick={() => {
              onClear()
              handleToggle()
            }}
            className='normal-case'
          >
            Eliminar archivos
          </Button>
          <Button color='blue' onClick={handleToggle} className='normal-case'>
            Cerrar
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

MassiveImportFilesList.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      filename: PropTypes.string,
      content_type: PropTypes.string
    })
  ),
  onClear: PropTypes.func,
  onRemoveFile: PropTypes.func,
  onAddFiles: PropTypes.func,
  accept: PropTypes.string,
  validationType: PropTypes.oneOf(['employee', 'asset', 'asset_v2'])
}

MassiveImportFilesList.defaultProps = {
  files: [],
  onClear: () => {},
  onRemoveFile: () => {},
  onAddFiles: () => {},
  accept: '',
  validationType: 'asset_v2'
}
